import React from 'react';
import { Box, Container, Typography, Chip, Grid, Card, CardContent, Divider } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ShieldIcon from '@mui/icons-material/Shield';
import BoltIcon from '@mui/icons-material/Bolt';
import CloudIcon from '@mui/icons-material/Cloud';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import PolicyIcon from '@mui/icons-material/Policy';
import VerifiedIcon from '@mui/icons-material/Verified';
import BarChartIcon from '@mui/icons-material/BarChart';
import GppGoodIcon from '@mui/icons-material/GppGood';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

const features = [
  {
    icon: <ShieldIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: '245+ Healthcare Rules',
    description: 'Comprehensive rule library covering HIPAA, FHIR, HITRUST, SOC 2, GDPR, and ISO 27001 — curated by compliance experts with deep healthcare domain knowledge.',
    details: ['85+ HIPAA rules mapped to 164.308/164.312', '40+ FHIR R4/R5 resource validation rules', '35+ HITRUST CSF control rules', '30+ SOC 2 Trust Services Criteria rules'],
  },
  {
    icon: <BoltIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Lightning Fast Scans',
    description: 'Complete compliance scans in under 5 seconds. Built for CI/CD — runs in your pipeline without adding perceptible latency to your deployments.',
    details: ['Sub-5-second scan execution', 'Parallel rule evaluation engine', 'Incremental scanning on changed files', 'Zero network latency — runs fully local'],
  },
  {
    icon: <CloudIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Multi-Cloud Support',
    description: 'Native support for healthcare cloud services across all major providers — validate configs before they reach production.',
    details: ['AWS HealthLake & S3 config scanning', 'Azure Health Data Services validation', 'GCP Healthcare API compliance checks', 'Terraform & CloudFormation support'],
  },
  {
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'CI/CD Integrations',
    description: 'Drop-in integrations for the tools your team already uses. Block non-compliant code before it merges.',
    details: ['GitHub Actions workflow step', 'Jenkins pipeline stage', 'Terraform pre-plan hook', 'Exit-code-based pass/fail for any CI system'],
  },
  {
    icon: <PolicyIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Audit-Ready Exports',
    description: 'Generate evidence packages your compliance team and auditors can use directly — no manual reformatting required.',
    details: ['Annotated PDF reports', 'Executive summary export', 'HIPAA 164.308/164.312 evidence mapping', 'JSON & CSV for custom tooling'],
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'FHIR Compliance',
    description: 'Deep FHIR R4/R5 validation covering resource conformance, HL7 standards, and secure data exchange patterns.',
    details: ['Resource profile conformance checks', 'Required field & terminology binding', 'Reference integrity validation', 'Capability statement coverage analysis'],
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Analytics Dashboard',
    description: 'Track compliance posture over time. Spot regressions early, measure improvement, and share progress with stakeholders.',
    details: ['Scan history and trend charts', 'Risk score tracking over time', 'Per-framework compliance breakdown', 'Team-level usage analytics'],
  },
  {
    icon: <GppGoodIcon sx={{ fontSize: 36, color: PRIMARY }} />,
    title: 'False Positive Management',
    description: 'Mark findings as accepted risks or false positives with justification. Keep your dashboard clean without losing audit history.',
    details: ['Per-finding false positive reporting', 'Justification & reviewer workflow', 'Audit trail of all overrides', 'False positive rate analytics'],
  },
];

const frameworks = [
  { name: 'HIPAA', description: 'Health Insurance Portability and Accountability Act', rules: '85+', color: '#2563eb' },
  { name: 'FHIR', description: 'Fast Healthcare Interoperability Resources R4/R5', rules: '40+', color: '#16a34a' },
  { name: 'HITRUST', description: 'Health Information Trust Alliance CSF', rules: '35+', color: '#9333ea' },
  { name: 'SOC 2', description: 'Service Organization Control 2', rules: '30+', color: '#dc2626' },
  { name: 'GDPR', description: 'General Data Protection Regulation', rules: '25+', color: '#ea580c' },
  { name: 'ISO 27001', description: 'Information Security Management', rules: '20+', color: '#0891b2' },
];

export function Features() {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box component="a" href="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>BaaStream</Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip label="Features" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }} />
          <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}>
            Everything your team needs to{' '}
            <Box component="span" sx={{ color: PRIMARY }}>stay compliant</Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            BaaStream ships 245+ healthcare compliance rules across 6 frameworks — fully automated, CI/CD-native, and built for engineering teams.
          </Typography>
        </Container>
      </Box>

      {/* Feature cards */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={3}>
          {features.map((f) => (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={f.title}>
              <Card sx={{ height: '100%', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255,117,24,0.1)' } }}>
                <CardContent sx={{ p: 3.5 }}>
                  <Box sx={{ mb: 2 }}>{f.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>{f.title}</Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, mb: 2 }}>{f.description}</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {f.details.map((d) => (
                      <Box component="li" key={d} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#555' }}>{d}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 10 }} />

        {/* Frameworks */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="Rule Library" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.1)', color: PRIMARY, fontWeight: 600, mb: 2 }} />
          <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 2 }}>
            6 compliance frameworks. 245+ rules.
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', maxWidth: 560, mx: 'auto' }}>
            Every rule is mapped to the exact regulation clause so you always know what's covered and why it matters.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {frameworks.map((fw) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={fw.name}>
              <Box sx={{ p: 3, border: `1px solid ${fw.color}22`, borderRadius: 2, bgcolor: `${fw.color}08` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Chip label={fw.name} size="small" sx={{ bgcolor: fw.color, color: '#fff', fontWeight: 700 }} />
                  <Chip label={`${fw.rules} rules`} size="small" variant="outlined" sx={{ color: fw.color, borderColor: fw.color, fontWeight: 600 }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#555' }}>{fw.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', mt: 10 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.6rem', md: '2rem' }, mb: 2 }}>
            Ready to get started?
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
            The Community plan is free forever. No credit card required.
          </Typography>
          <Box component="a" href="/register" sx={{ display: 'inline-block', px: 5, py: 1.75, bgcolor: PRIMARY, color: '#fff', borderRadius: 2, fontWeight: 700, textDecoration: 'none', fontSize: '1rem', '&:hover': { opacity: 0.9 } }}>
            Start for free
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} Monks Do Work. All rights reserved.</Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>← Back to Home</Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
